import { useMemo, useState, type ChangeEvent } from 'react';
import {
  Alert, AlertTitle, Box, Chip, Divider, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Paper,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth/AuthContext';
import { useDaniaRestauracji, useMenuDnia, useSzkolyRestauracji, useZdolnosci } from '@/lib/useAnalytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * MENU NA KONKRETNY DZIEŃ — PLANER OPARTY NA GRAFIKU, NIE NA WYOBRAŹNI
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ MODEL WZIĘTY ZE ŹRÓDŁA, NIE WYMYŚLONY ─────────────────────────────────────────────────────┐
 * │ Ziarnem menu dziennego w systemie głównym jest (RESTAURACJA, SZKOŁA, DZIEŃ) → zbiór dań.    │
 * │ Restauracje KOPIUJĄ kartę na kolejne dni i szkoły (`CopyDailyMenu`), więc jedno danie       │
 * │ katalogu daje wiele wierszy menu — 13 tożsamości dało 364 wiersze w pomiarze referencyjnym. │
 * │                                                                                              │
 * │ Dwa ograniczenia są TWARDE i pochodzą z grafiku, nie z tego ekranu:                          │
 * │   • dzień musi być dniem dostawy dla pary szkoła–restauracja                                 │
 * │     (`SchoolRestaurantAvailability.DayOfWeek`),                                              │
 * │   • zamówienia przyjmowane są do godziny granicznej (`SchoolRestaurant.OrderFinishTime`).    │
 * │                                                                                              │
 * │ Ekran POKAZUJE te ograniczenia zamiast pozwalać zaplanować menu na dzień, w którym i tak     │
 * │ nikt nie dowiezie. Plan, którego nie da się zrealizować, jest gorszy niż brak planu.         │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ CZEGO TU JESZCZE NIE MA I DLACZEGO MÓWIMY TO WPROST ───────────────────────────────────────┐
 * │ ZAPIS menu wymaga API systemu głównego, którego nie ma (strumień R5-03). ODCZYT istniejącego │
 * │ menu wymaga repliki, której w CRM nie ma (strumień R5-04) — sprawdzone w schemacie repliki.  │
 * │                                                                                              │
 * │ Dlatego ekran planuje i waliduje, ale nie udaje, że zapisuje. Przycisk, który nic nie robi,  │
 * │ i pusta tabela udająca „nie ma menu" kosztują tyle samo: wysyłają człowieka szukać usterki,  │
 * │ której nie ma.                                                                                │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 */

/** Kodowanie dni z repliki grafiku: 0 = poniedziałek … 6 = niedziela. NIE jest to kodowanie .NET. */
const DNI_PELNE = ['poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota', 'niedziela'];

/** `Date.getDay()` daje 0 = niedziela. Replika liczy od poniedziałku — stąd przesunięcie. */
function dzienTygodnia(iso: string): number | null {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return (d.getDay() + 6) % 7;
}

function dzisiajISO(): string {
  return new Date().toLocaleDateString('en-CA');
}

export function MenuRestauracjiPage() {
  const auth = useAuth();
  const czyWlasciciel = auth.has('admin.users.read');

  const [restauracja, setRestauracja] = useState('');
  const [dzien, setDzien] = useState(dzisiajISO());

  const wskazana = restauracja.trim() === '' ? undefined : Number(restauracja);
  const brakWskazania = czyWlasciciel && wskazana === undefined;

  const szkoly = useSzkolyRestauracji(wskazana);
  const menuDnia = useMenuDnia(dzien, wskazana);
  const dania = useDaniaRestauracji(wskazana);
  const zdolnosci = useZdolnosci();

  const zapisMenu = zdolnosci.data?.zdolnosci?.menu_zapis === true;
  const zapisDan = zdolnosci.data?.zdolnosci?.dania_zapis === true;

  const numerDnia = dzienTygodnia(dzien);

  /*
   * Podział szkół na te, które TEGO DNIA odbierają dostawę, i pozostałe. Robimy to z danych
   * grafiku, a nie z założenia „pewnie wszystkie" — para szkoła–restauracja może mieć dostawy
   * tylko w wybrane dni i to jest ustalenie organizacyjne, nie szczegół.
   */
  const podzial = useMemo(() => {
    const lista = szkoly.data?.szkoly ?? [];
    if (numerDnia === null) return { tegoDnia: [], pozostale: lista };
    return {
      tegoDnia: lista.filter((s) => s.dni_aktywne.includes(numerDnia)),
      pozostale: lista.filter((s) => !s.dni_aktywne.includes(numerDnia)),
    };
  }, [szkoly.data, numerDnia]);

  const pozycjeKarty = dania.data?.dania ?? [];
  const zrodloKartyPodlaczone = dania.data?.zrodlo_podlaczone !== false;

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Menu"
        opis="Menu na konkretny dzień, w szkołach przypisanych do Twojej restauracji."
      />

      {/* ── WYBÓR DNIA ────────────────────────────────────────────────────────────────────── */}
      <Karta>
        <Stack direction="row" spacing={2} sx={{ p: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {czyWlasciciel && (
            <TextField
              label="Restauracja (nr)" size="small" sx={{ width: 170 }} value={restauracja}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRestauracja(e.target.value)}
              helperText="Konto właściciela nie ma własnej restauracji"
            />
          )}
          <TextField
            label="Dzień menu" type="date" size="small" value={dzien}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDzien(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {numerDnia !== null && (
            <Typography sx={{ fontFamily: 'var(--krój-data)', color: 'var(--tekst-2)' }}>
              {DNI_PELNE[numerDnia]}
            </Typography>
          )}
        </Stack>
      </Karta>

      {brakWskazania && (
        <EmptyState
          title="Wskaż restaurację"
          description="Konto właściciela nie jest przypisane do restauracji, więc trzeba podać, czyje menu układasz."
        />
      )}

      {!brakWskazania && szkoly.isLoading && <LoadingState />}
      {!brakWskazania && szkoly.error ? (
        <ErrorState error={szkoly.error} context="Menu — szkoły i dni dostaw." />
      ) : null}


      {/* ── WYSTAWIONE TEGO DNIA — PARYTET ZE STARYM PANELEM ──────────────────────────────── */}
      {/*
        Źródłem jest replika dbo.DailyMenus/DailyPosition systemu głównego. Cztery stany źródła
        są rozróżniane SŁOWEM (wymóg właściciela): puste-biznesowo ≠ brak repliki ≠ brak GRANT
        ≠ nieświeży sync. Milczące zero przy niedostępnym źródle jest tu zakazane.
      */}
      {!brakWskazania && (
        <Karta>
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 800 }}>Wystawione tego dnia</Typography>

            {menuDnia.isLoading && <LoadingState />}
            {menuDnia.error ? (
              <ErrorState error={menuDnia.error} context="Menu dnia — pozycje wystawione w systemie." />
            ) : null}

            {!menuDnia.isLoading && !menuDnia.error && menuDnia.data && (() => {
              const md = menuDnia.data;
              // Odpowiedź bez kontraktu (starszy backend, atrapa) = źródło niewidoczne dla frontu.
              const stanZrodla = md.zrodlo?.stan ?? 'SOURCE_UNAVAILABLE';
              if (stanZrodla === 'SOURCE_UNAVAILABLE') {
                return (
                  <Alert severity="info">
                    <AlertTitle>Źródło menu dziennego jeszcze niepodłączone</AlertTitle>
                    {md.zaleznosc
                      ? `Czekamy na: ${md.zaleznosc.czego_brak} (${md.zaleznosc.strumien}).`
                      : 'Replika menu dziennego nie została jeszcze dostarczona.'}
                    {' '}To nie znaczy, że restauracja nic nie wystawiła — znaczy, że panel
                    jeszcze tego nie widzi.
                  </Alert>
                );
              }
              if (stanZrodla === 'ACCESS_DENIED') {
                return (
                  <Alert severity="warning">
                    <AlertTitle>Brak uprawnienia do repliki menu</AlertTitle>
                    Replika istnieje, ale konto odczytowe panelu nie ma do niej dostępu
                    {md.zaleznosc ? ` (${md.zaleznosc.czego_brak})` : ''}.
                  </Alert>
                );
              }
              return (
                <Stack spacing={1.5}>
                  {stanZrodla === 'SYNC_STALE' && (
                    <Alert severity="warning">
                      <AlertTitle>Dane mogą być nieaktualne</AlertTitle>
                      Ostatnia synchronizacja: {md.zrodlo?.swiezosc
                        ? new Date(md.zrodlo!.swiezosc!).toLocaleString('pl-PL')
                        : 'nieznana (feed nigdy nie przebiegł)'}. Pokazujemy ostatni znany stan.
                    </Alert>
                  )}

                  {(md.pozycje ?? []).length === 0 ? (
                    <Alert severity="info">
                      <AlertTitle>Nic nie wystawiono na ten dzień</AlertTitle>
                      Źródło działa i jest świeże — na {md.data} ta restauracja nie ma ułożonego
                      menu w systemie. To odpowiedź biznesowa, nie awaria.
                    </Alert>
                  ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                      <Table size="small" aria-label="Pozycje menu wystawione w wybranym dniu">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Danie</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Opis</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Cena</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Sztuk</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Oznaczenia</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(md.pozycje ?? []).map((poz) => (
                            <TableRow key={poz.meal_id} hover>
                              <TableCell>{poz.nazwa ?? `danie #${poz.meal_id}`}</TableCell>
                              <TableCell sx={{ color: 'var(--tekst-2)' }}>{poz.opis ?? '—'}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: 'var(--krój-data)', whiteSpace: 'nowrap' }}>
                                {poz.cena_grosze === null ? '—' : `${(poz.cena_grosze / 100).toFixed(2)} zł`}
                              </TableCell>
                              <TableCell align="right" sx={{ fontFamily: 'var(--krój-data)' }}>
                                {poz.sztuk ?? '—'}
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5}>
                                  {poz.danie_dnia && <Chip size="small" color="success" label="danie dnia" />}
                                  {poz.nowe && <Chip size="small" color="info" variant="outlined" label="nowe" />}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {(md.szkoly ?? []).length > 0 && (
                    <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
                      Menu tego dnia obejmuje szkoły:{' '}
                      {(md.szkoly ?? []).map((sz) => sz.nazwa ?? `#${sz.szkola_id}`).join(' · ')}
                    </Typography>
                  )}
                </Stack>
              );
            })()}
          </Stack>
        </Karta>
      )}

      {/* ── GDZIE TO MENU POJEDZIE TEGO DNIA ──────────────────────────────────────────────── */}
      {!brakWskazania && !szkoly.isLoading && !szkoly.error && (
        <Karta>
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 800 }}>
              Szkoły z dostawą {numerDnia !== null ? `w ${DNI_PELNE[numerDnia]}` : ''}
            </Typography>

            {podzial.tegoDnia.length === 0 ? (
              <Alert severity="warning">
                <AlertTitle>Tego dnia nie ma dostaw</AlertTitle>
                Żadna z Twoich szkół nie odbiera dostawy w wybranym dniu. Menu na ten dzień nie ma
                gdzie pojechać — wybierz inny dzień albo sprawdź grafik w Administracji.
              </Alert>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Szkoły z dostawą w wybranym dniu">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Szkoła</TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Zamówienia do</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Uwaga</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {podzial.tegoDnia.map((s) => {
                      /*
                       * Godzina graniczna dotyczy ZAMAWIANIA przez ucznia, nie układania menu.
                       * Pokazujemy ją, bo menu ułożone po cutoffie na dziś nikomu się nie
                       * pokaże — i lepiej wiedzieć to przed, niż po.
                       */
                      const dzisiaj = dzien === dzisiajISO();
                      const poCzasie = dzisiaj && s.godzina_jednolita && s.godzina
                        ? new Date().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) > s.godzina
                        : false;
                      return (
                        <TableRow key={s.szkola_id} hover>
                          <TableCell>{s.szkola}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'var(--krój-data)' }}>
                            {s.godzina_jednolita
                              ? (s.godzina ?? '—')
                              : <Chip size="small" color="warning" variant="outlined" label="różna w różne dni" />}
                          </TableCell>
                          <TableCell>
                            {poCzasie
                              ? <Chip size="small" color="warning" label="godzina zamówień minęła" />
                              : <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)' }}>—</Typography>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {podzial.pozostale.length > 0 && (
              <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
                Pozostałe {podzial.pozostale.length} szkół nie ma dostawy w tym dniu — menu ich nie
                dotyczy. Dni dostaw ustala Administracja → Grafik.
              </Typography>
            )}
          </Stack>
        </Karta>
      )}

      {/* ── KARTA DAŃ, Z KTÓREJ SIĘ UKŁADA ────────────────────────────────────────────────── */}
      {!brakWskazania && (
        <Karta>
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 800 }}>Karta dań</Typography>

            {dania.isLoading && <LoadingState />}
            {dania.error ? <ErrorState error={dania.error} context="Menu — karta dań." /> : null}

            {!dania.isLoading && !dania.error && !zrodloKartyPodlaczone && (
              <Alert severity="info">
                <AlertTitle>Karta dań oczekuje na integrację</AlertTitle>
                {dania.data?.zaleznosc
                  ? (
                    <>
                      Brakuje: <strong>{dania.data.zaleznosc.czego_brak}</strong>{' '}
                      (strumień {dania.data.zaleznosc.strumien}). {dania.data.zaleznosc.skutek}
                    </>
                  )
                  : 'Źródło katalogu dań nie jest jeszcze podłączone do tej konsoli.'}
              </Alert>
            )}

            {zrodloKartyPodlaczone && !dania.isLoading && !dania.error && pozycjeKarty.length === 0 && (
              <EmptyState
                title="Karta dań jest pusta"
                description="Katalog jest podłączony, ale nie ma w nim ani jednej pozycji tej restauracji."
              />
            )}

            {pozycjeKarty.length > 0 && (
              <Typography variant="body2" sx={{ color: 'var(--tekst-2)' }}>
                W karcie jest <strong>{pozycjeKarty.length}</strong> pozycji. Z nich układa się menu
                na wybrany dzień — dla każdej szkoły z dostawą osobno.
              </Typography>
            )}
          </Stack>
        </Karta>
      )}

      {/* ── CZEGO BRAKUJE DO ZAPISU ───────────────────────────────────────────────────────── */}
      <Karta>
        <Stack spacing={1.5} sx={{ p: 2 }}>
          <Typography sx={{ fontWeight: 800 }}>Układanie menu — stan podłączenia</Typography>

          <Alert severity={zapisMenu ? 'success' : 'info'}>
            <AlertTitle>
              {zapisMenu ? 'Zapis menu jest podłączony' : 'Zapis menu oczekuje na integrację'}
            </AlertTitle>
            <Typography variant="body2">
              {zapisMenu
                ? 'Zmiany menu trafiają do systemu głównego i przechodzą przez moderację.'
                : 'Układanie menu wymaga zapisu do systemu głównego TuSzamy. Ta droga nie jest jeszcze '
                  + 'podłączona, więc ekran planuje i sprawdza ograniczenia, ale nie zapisuje. '
                  + 'Nie pokazujemy przycisku, który nic nie robi.'}
            </Typography>
          </Alert>

          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <Typography component="li" variant="body2">
              Zapis menu: <strong>{zapisMenu ? 'dostępny' : 'niedostępny'}</strong>
            </Typography>
            <Typography component="li" variant="body2">
              Zapis dań: <strong>{zapisDan ? 'dostępny' : 'niedostępny'}</strong>
            </Typography>
          </Box>

          <Divider />

          {/*
            Reguły, które będą obowiązywać PO podłączeniu zapisu. Wypisane teraz, bo są to
            ograniczenia SYSTEMU, a nie tego ekranu — i nie znikną wraz z integracją.
          */}
          <Typography sx={{ fontWeight: 800, fontSize: 14 }}>Co i tak będzie obowiązywać</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <Typography component="li" variant="body2">
              Menu można ułożyć wyłącznie w szkołach już przypisanych do restauracji. Przypisania
              nadaje Administracja — restauracja nie nadaje ich sobie sama.
            </Typography>
            <Typography component="li" variant="body2">
              Tylko w dniach dostawy dla danej pary szkoła–restauracja.
            </Typography>
            <Typography component="li" variant="body2">
              Każda zmiana przechodzi przez moderację. Zgłoszenie zaczyna jako <strong>oczekujące</strong>,
              a nie zatwierdzone — i nie ma drogi, która by to omijała.
            </Typography>
            <Typography component="li" variant="body2">
              Podgląd właściciela jest tylko do odczytu. Zmiany wykonuje się we własnym koncie,
              gdzie widać, kto je wprowadził.
            </Typography>
          </Box>
        </Stack>
      </Karta>
    </Stack>
  );
}

export default MenuRestauracjiPage;
